import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import { ListViewFooter } from '../../general_components/content_management/list_view/listViewFooter';
import popupStyles from '../../general_components/content_management/reusableItems/itemOnPopup.module.scss';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { can } from '../../supplementary_components/userDataDelegate';
import { DATAQ_OPTIONS, RESULT_OPTIONS, errorMessage, formatIsoDate, labelFor, safetyApi } from './safetyApi';
import { openCitationForm, openInspectionForm } from './inspectionForms';
import { ViolationCards, inspectionListLabel } from './safetyRecordPopup';
import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';
import { openAttachmentsPopup } from '../../general_components/attachmentsPopup/attachmentsPopup';
import { entityHasAttachments } from '../../general_components/attachmentsPopup/supplementaryFilesListing';
import { ATTACHMENT_ENTITY_TYPES } from '../../general_components/attachmentsPopup/attachmentEntityConfig';
import reportListStyles from '../reports/payableInvoicesList.module.scss';
import moneyCodeListStyles from '../reports/moneyCodeList.module.scss';
import styles from './inspections.module.scss';

const CATEGORIES = [
    { key: 'inspections', label: 'Inspections' },
    { key: 'citations', label: 'Citations' },
];

function dash(value) {
    return value == null || value === '' ? '-' : value;
}

function lockColumnWidths(template) {
    return template.trim().split(/\s+/).map((size) => (
        size.endsWith('fr') ? `minmax(0, ${size})` : `minmax(${size}, ${size})`
    )).join(' ');
}

function copyListValue(event, value) {
    event.stopPropagation();
    const text = value == null ? '' : String(value).trim();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
        addNotification({ message: 'Copied to clipboard', type: 'notify', duration: 1000 });
    });
}

function CopyClip({ value }) {
    const text = value == null || String(value).trim() === '' ? '' : String(value);
    if (!text) return <span className={`${styles.clipCell} ${styles.clipInline}`}>-</span>;
    return (
        <button
            type="button"
            className={`${styles.clipCell} ${styles.clipInline} ${styles.copyValue}`}
            title={text}
            onClick={(event) => copyListValue(event, text)}
        >
            {text}
        </button>
    );
}

function openCitationPopup(citationId) {
    safetyApi.getCitation(citationId).then((record) => {
        addPopup({
            content: CitationListPopup,
            popupData: { record },
            customWidth: '90%',
            customMaxWidth: '1700px',
            customMinWidth: '880px',
            EZCloseAllowed: true,
        });
    }).catch((loadError) => addNotification({ message: errorMessage(loadError), type: 'alert' }));
}

function openInspectionPopup(inspectionId) {
    safetyApi.getInspection(inspectionId).then((record) => {
        addPopup({
            content: InspectionListPopup,
            popupData: { record },
            customWidth: '90%',
            customMaxWidth: '1700px',
            customMinWidth: '880px',
            EZCloseAllowed: true,
        });
    }).catch((loadError) => addNotification({ message: errorMessage(loadError), type: 'alert' }));
}

function CitationListPopup({ popupData }) {
    const canManage = can('INSPECTIONS', 'MANAGE');
    const [record, setRecord] = useState(popupData.record);
    return (
        <div className={popupStyles.itemOnPopup} onClick={(event) => event.stopPropagation()}>
            <div className={listStyles.pageHeader}>
                <div>Citation</div>
            </div>
            <CitationList
                rows={[record]}
                links={false}
                attachments
                showActions={false}
                canManage={canManage}
                onChanged={() => {}}
                onFiles={(id, files) => setRecord((current) => ({ ...current, files }))}
                onDataQ={(saved) => setRecord((current) => ({ ...current, ...saved }))}
            />
        </div>
    );
}

function InspectionListPopup({ popupData }) {
    const record = {
        ...popupData.record,
        violationCount: popupData.record?.violations?.length || 0,
        files: popupData.record?.files ?? [],
    };
    return (
        <div className={popupStyles.itemOnPopup} onClick={(event) => event.stopPropagation()}>
            <div className={listStyles.pageHeader}>
                <div>Inspection</div>
            </div>
            <InspectionList rows={[record]} canManage={false} onFiles={() => {}} />
        </div>
    );
}

function confirmDelete(title, message, action) {
    addPopup({
        content: ConfirmationDialogue,
        EZCloseAllowed: true,
        popupData: {
            confirmationDialogue: true,
            formTitle: title,
            submitTitle: 'Delete',
            name: 'Confirm',
            message,
            action: (done) => {
                action()
                    .then(() => done(true))
                    .catch((error) => {
                        addNotification({ message: errorMessage(error), type: 'alert' });
                        done(true);
                    });
            },
            cancelAction: (closePopup) => closePopup(),
        },
    });
}

export function InspectionsPage() {
    const canView = can('INSPECTIONS', 'VIEW');
    const canManage = can('INSPECTIONS', 'MANAGE');
    const [category, setCategory] = useState('inspections');
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({ currentPageNumber: 0 });
    const [reloadToken, setReloadToken] = useState(0);
    const loadRequest = React.useRef(0);
    const paginationRef = React.useRef(pagination);

    const load = useCallback(() => {
        const requestId = ++loadRequest.current;
        const page = paginationRef.current.currentPageNumber || 0;
        setRows(null);
        setError('');
        const request = category === 'inspections'
            ? safetyApi.listInspections(page)
            : safetyApi.listCitations(page);
        request.then((data) => {
            if (loadRequest.current !== requestId) return;
            const list = Array.isArray(data?.dataset) ? data.dataset : [];
            if (list.length === 0 && page > 0) {
                paginationRef.current = { ...paginationRef.current, currentPageNumber: page - 1 };
                setPagination(paginationRef.current);
                setReloadToken((token) => token + 1);
                return;
            }
            const pageInfo = { ...(data || {}) };
            delete pageInfo.dataset;
            paginationRef.current = pageInfo;
            setPagination(pageInfo);
            setRows(list);
            if (category !== 'inspections') return;
            list.forEach((row) => {
                safetyApi.getInspection(row.id).then((detail) => {
                    if (loadRequest.current !== requestId) return;
                    setRows((current) => (Array.isArray(current)
                        ? current.map((item) => (item.id === row.id ? { ...item, files: detail?.files ?? [], notes: detail?.notes ?? '' } : item))
                        : current));
                }).catch(() => {});
            });
        }).catch((loadError) => {
            if (loadRequest.current !== requestId) return;
            setError(errorMessage(loadError));
        });
    }, [category]);

    useEffect(() => {
        paginationRef.current = { currentPageNumber: 0 };
        setPagination(paginationRef.current);
    }, [category]);

    useEffect(() => {
        if (canView) load();
    }, [canView, load, reloadToken]);

    if (!canView) {
        return <div className="errorMessage">You do not have access to this page.</div>;
    }

    const openInspection = (summary) => {
        if (!summary) {
            openInspectionForm(null, load);
            return;
        }
        sendRequest('GET', `${API_ROOT}inspections/${summary.id}`, null, (record) => {
            openInspectionForm(record, load);
        }, (loadError) => addNotification({ message: errorMessage(loadError), type: 'alert' }));
    };

    return (
        <>
            <div className={listStyles.pageHeader}>
                <div>Inspections</div>
                <div className={listStyles.actionsContainer}>
                    {canManage ? (
                        <div
                            className={`${listStyles.addButton} non-selectable`}
                            onClick={() => (category === 'inspections'
                                ? openInspection(null)
                                : openCitationForm({ onSaved: load }))}
                        >
                            Add {category === 'inspections' ? 'Inspection' : 'Citation'} +
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={listStyles.catSelector}>
                {CATEGORIES.map((item) => (
                    <div
                        key={item.key}
                        className={category === item.key ? listStyles.selected : ''}
                        onClick={() => setCategory(item.key)}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
            {rows == null && !error ? <LoadingIcon state height="10rem" /> : null}
            {error ? <div className="errorMessage">{error}</div> : null}
            {rows ? (
                category === 'inspections'
                    ? <InspectionList rows={rows} canManage={canManage} onEdit={openInspection} onChanged={load} onFiles={(id, files) => setRows((current) => current?.map((item) => (item.id === id ? { ...item, files } : item)))} />
                    : <CitationList rows={rows} canManage={canManage} onChanged={load} onDataQ={(saved) => setRows((current) => current?.map((item) => (item.id === saved.id ? { ...item, ...saved } : item)))} onFiles={(id, files) => setRows((current) => current?.map((item) => (item.id === id ? { ...item, files } : item)))} />
            ) : null}
            {rows ? (
                <ListViewFooter
                    paginationData={{
                        data: pagination,
                        setPaginationData: (update) => {
                            const next = update(paginationRef.current);
                            paginationRef.current = next;
                            setPagination({ ...next });
                        },
                    }}
                    resultCount={rows.length}
                    requestReload={() => setReloadToken((token) => token + 1)}
                />
            ) : null}
        </>
    );
}

function Actions({ canManage, onEdit, onRemove }) {
    if (!canManage) return <span />;
    return (
        <ThreeDotsButton
            position="relative"
            onEdit={onEdit}
            onRemove={onRemove}
        />
    );
}

function AttachmentsCell({ files, canEdit, entityType, entity, titleLabel, onFiles, links = true }) {
    if (files == null) {
        return <span className={`${moneyCodeListStyles.attachmentsDash} ${styles.centeredCell}`}>-</span>;
    }
    const uploaded = entityHasAttachments(files);
    if (!links) {
        const labelClass = `${uploaded ? reportListStyles.seeAttachmentsButton : reportListStyles.paperworkMissingButton} ${styles.centeredCell} ${styles.staticAttachment}`;
        return <span className={labelClass}>{uploaded ? 'See attachments' : 'Missing'}</span>;
    }
    const openPopup = (event) => {
        event.stopPropagation();
        openAttachmentsPopup({
            entityType,
            entity: { ...entity, files },
            titleLabel,
            canEdit,
            onEntityUpdated: (updated) => onFiles(updated?.files ?? []),
        });
    };
    const buttonClass = `${uploaded ? reportListStyles.seeAttachmentsButton : reportListStyles.paperworkMissingButton} ${styles.centeredCell}`;
    return (
        <button type="button" className={buttonClass} onClick={openPopup}>
            {uploaded ? 'See attachments' : 'Missing'}
        </button>
    );
}

function InspectionList({ rows, canManage, onEdit, onChanged, onFiles }) {
    const columns = lockColumnWidths('0.7fr 0.5fr 0.5fr 1fr 0.8fr 0.8fr 0.5fr 1.4fr 1.7fr 0.9fr 1.5fr 1.5fr 2.6rem');
    const centered = new Set(['State', 'Level', 'Severity Weight', 'Citations', 'Violations', 'Notes', 'Attachments']);
    return (
        <div className={`${listStyles.container} ${styles.stableList}`} style={{ '--list-columns': columns }}>
            <div className={listStyles.listViewHeaderWrapper}>
                {['Number', 'Date', 'State', 'Driver', 'Truck', 'Trailer', 'Level', 'Citations', 'Violations', 'Severity Weight', 'Notes', 'Attachments', ''].map((label) => (
                    <div className={`${listStyles.listHeaderLabel} ${centered.has(label) ? styles.centeredHeader : ''}`} key={label || 'actions'}><span className={listStyles.labelText}>{label}</span></div>
                ))}
            </div>
            {rows.length === 0 ? <EmptyRow>No inspections yet</EmptyRow> : null}
            {rows.map((row) => (
                <div className={`${listStyles.row} ${styles.inspectionRow} ${!row.violationCount ? styles.cleanRow : ''}`} key={row.id}>
                    <CopyClip value={row.inspectionNumber} />
                    <span>{formatIsoDate(row.inspectionDate)}</span>
                    <span className={styles.centeredCell}>{dash(row.state)}</span>
                    <span className={styles.partyValue}>{row.driverId ? <PopupOnClick text={row.driverName} itemType="driver" itemId={row.driverId} alwaysUnderlined /> : dash(row.driverName)}</span>
                    <span className={styles.partyValue}>{row.truckId ? <PopupOnClick text={row.truckNumber} itemType="truck" itemId={row.truckId} alwaysUnderlined /> : dash(row.truckNumber)}</span>
                    <span>{dash(row.trailerNumber)}</span>
                    <span className={styles.centeredCell}>{row.level}</span>
                    <span className={`${styles.centeredStack} ${styles.clipCell}`}>
                        {row.citations?.length ? row.citations.map((citation) => (
                            <button key={citation.id} type="button" className={`${styles.recordLink} ${styles.clipInline}`} title={citation.ticketNumber || 'Citation'} onClick={() => openCitationPopup(citation.id)}>
                                {citation.ticketNumber || 'Citation'}
                            </button>
                        )) : <span className={styles.clipInline}>-</span>}
                    </span>
                    <span className={styles.violationsCell}>
                        <ViolationCards violations={row.violationCount ? row.violations : []} />
                    </span>
                    <span className={styles.centeredCell}>{row.severityWeight}</span>
                    <span className={`${styles.noteText} ${styles.centeredCell}`}>{dash(row.notes)}</span>
                    <AttachmentsCell
                            files={row.files}
                            canEdit={canManage}
                            entityType={ATTACHMENT_ENTITY_TYPES.INSPECTION}
                            entity={row}
                            titleLabel={row.inspectionNumber || String(row.id)}
                            onFiles={(files) => onFiles(row.id, files)}
                    />
                    <Actions
                        canManage={canManage}
                        onEdit={() => onEdit(row)}
                        onRemove={() => confirmDelete(
                            'Delete Inspection',
                            'Are you sure you want to delete this inspection? Its citations will be deleted too.',
                            () => safetyApi.deleteInspection(row.id).then(() => {
                                addNotification({ message: 'Inspection was removed', type: 'success' });
                                onChanged();
                            }),
                        )}
                    />
                </div>
            ))}
        </div>
    );
}

function EmptyRow({ children }) {
    return <div className={styles.empty} style={{ display: 'flex', justifyContent: 'center' }}><span>{children}</span></div>;
}

function DataQCell({ row, canManage, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const saving = useRef(false);
    const openedAt = useRef(0);
    const selectRef = useRef(null);

    if (!canManage) {
        return <span>{labelFor(DATAQ_OPTIONS, row.dataQStatus)}</span>;
    }
    if (!editing) {
        return (
            <button
                type="button"
                className={styles.recordLink}
                onClick={(event) => {
                    event.stopPropagation();
                    openedAt.current = Date.now();
                    flushSync(() => setEditing(true));
                    try {
                        selectRef.current?.showPicker();
                    } catch {
                        selectRef.current?.focus();
                    }
                }}
            >
                {labelFor(DATAQ_OPTIONS, row.dataQStatus)}
            </button>
        );
    }
    return (
        <select
            ref={selectRef}
            className={styles.dataQSelect}
            defaultValue={row.dataQStatus || 'NOT_STARTED'}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                if (event.key === 'Escape') setEditing(false);
            }}
            onChange={(event) => {
                const next = event.target.value;
                if (next === (row.dataQStatus || 'NOT_STARTED')) {
                    setEditing(false);
                    return;
                }
                saving.current = true;
                safetyApi.saveCitation(row.id, {
                    ticketNumber: row.ticketNumber || null,
                    issuedDate: row.inspectionId ? null : (row.date || null),
                    driverId: row.driverId ?? null,
                    truckId: row.truckId ?? null,
                    trailerId: row.trailerId ?? null,
                    state: row.state || null,
                    dataQStatus: next,
                    reason: row.reason,
                    lawyerId: row.lawyerId ?? null,
                    lawyerName: row.lawyerName || null,
                    trialDate: row.trialDate || null,
                    result: row.result,
                    notes: row.notes || null,
                }).then((saved) => {
                    onUpdated?.(saved);
                    setEditing(false);
                }).catch((saveError) => {
                    addNotification({ message: errorMessage(saveError), type: 'alert' });
                }).finally(() => {
                    saving.current = false;
                });
            }}
            onBlur={() => {
                window.setTimeout(() => {
                    if (saving.current) return;
                    if (Date.now() - openedAt.current < 400) return;
                    setEditing(false);
                }, 200);
            }}
        >
            {DATAQ_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    );
}

function resultTone(value) {
    if (value === 'PENDING') return styles.resultPending;
    if (value === 'CONVICTED_ORIGINAL' || value === 'CONVICTED_DIFFERENT') return styles.resultConvicted;
    if (value === 'DISMISSED') return styles.resultDismissed;
    if (value === 'PAID') return styles.resultPaid;
    return '';
}

function CitationList({ rows, canManage, onChanged, onFiles, onDataQ, links = true, attachments = true, showActions = true }) {
    const columns = lockColumnWidths('0.4fr 0.5fr 0.4fr 1.4fr 0.7fr 0.8fr 0.8fr 1.4fr 0.7fr 0.7fr 1.5fr 1.2fr 1.5fr 2.6rem');
    const centered = new Set(['DataQ', 'Inspection', 'Notes', 'Attachments', 'Result']);
    return (
        <div className={`${listStyles.container} ${styles.stableList}`} style={{ '--list-columns': columns }}>
            <div className={listStyles.listViewHeaderWrapper}>
                {['Date', 'Ticket', 'State', 'Driver Name', 'Truck', 'Inspection', 'DataQ', 'Reason', 'Lawyer', 'Trial Date', 'Notes', 'Attachments', 'Result', ''].map((label) => (
                    <div className={`${listStyles.listHeaderLabel} ${centered.has(label) ? styles.centeredHeader : ''}`} key={label || 'actions'}><span className={listStyles.labelText}>{label}</span></div>
                ))}
            </div>
            {rows.length === 0 ? <EmptyRow>No citations yet</EmptyRow> : null}
            {rows.map((row) => (
                <div className={listStyles.row} key={row.id}>
                    <span>{formatIsoDate(row.date)}</span>
                    <CopyClip value={row.ticketNumber} />
                    <span>{dash(row.state)}</span>
                    <span className={styles.partyValue}>{links && row.driverId ? <PopupOnClick text={row.driverName} itemType="driver" itemId={row.driverId} alwaysUnderlined /> : dash(row.driverName)}</span>
                    <span className={styles.partyValue}>{links && row.truckId ? <PopupOnClick text={row.truckNumber} itemType="truck" itemId={row.truckId} alwaysUnderlined /> : dash(row.truckNumber)}</span>
                    <span className={`${styles.centeredCell} ${styles.clipCell}`}>
                        {inspectionListLabel(row) ? (
                            links ? (
                                <button type="button" className={`${styles.recordLink} ${styles.clipInline}`} title={inspectionListLabel(row)} onClick={() => openInspectionPopup(row.inspectionId)}>
                                    {inspectionListLabel(row)}
                                </button>
                            ) : <span className={styles.clipInline} title={inspectionListLabel(row)}>{inspectionListLabel(row)}</span>
                        ) : <span className={styles.clipInline}>-</span>}
                    </span>
                    <span className={styles.centeredCell}><DataQCell row={row} canManage={canManage} onUpdated={onDataQ} /></span>
                    <span className={styles.noteText}>{dash(row.reason)}</span>
                    <span>{dash(row.lawyerName)}</span>
                    <span>{formatIsoDate(row.trialDate)}</span>
                    <span className={`${styles.noteText} ${styles.centeredCell}`}>{dash(row.notes)}</span>
                    <AttachmentsCell
                        files={row.files ?? []}
                        canEdit={canManage}
                        links={attachments}
                        entityType={ATTACHMENT_ENTITY_TYPES.CITATION}
                        entity={row}
                        titleLabel={row.ticketNumber || String(row.id)}
                        onFiles={(files) => onFiles?.(row.id, files)}
                    />
                    <span className={styles.centeredCell}>
                        <span className={resultTone(row.result)}>{labelFor(RESULT_OPTIONS, row.result)}</span>
                    </span>
                    <Actions
                        canManage={showActions && canManage}
                        onEdit={() => openCitationForm({
                            record: row,
                            onSaved: onChanged,
                        })}
                        onRemove={() => confirmDelete(
                            'Delete Citation',
                            'Are you sure you want to delete this citation?',
                            () => safetyApi.deleteCitation(row.id).then(() => {
                                addNotification({ message: 'Citation was removed', type: 'success' });
                                onChanged();
                            }),
                        )}
                    />
                </div>
            ))}
        </div>
    );
}
